import { useState } from 'react'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import {
  getGetApiCompaniesCurrentTranslationsEntityTypeEntityIdPropertyQueryKey,
  getGetApiCompaniesCurrentTranslationsEntityTypeEntityIdPropertyQueryOptions,
  usePatchApiCompaniesCurrentTranslationsEntityTypeEntityIdProperty,
} from '@/api/generated/company-management-user/company-management-user'
import type { LocalizationEntityType } from '@/api/model/localizationEntityType'

interface Options {
  entityType: LocalizationEntityType
  /** undefined on create — there's nothing to load yet, save() receives the Id later */
  entityId?: string
  /** Must stay stable over the component's lifetime (hook order in useQueries) */
  properties: string[]
  enabled?: boolean
}

/**
 * Buffers translations per property (language → text) for an entity.
 * In the edit case existing translations are loaded; changes stay local
 * until save(entityId) persists them via PATCH after the object is saved —
 * one PATCH per actually changed property.
 */
export function useEntityTranslations({ entityType, entityId, properties, enabled = true }: Options) {
  const queryClient = useQueryClient()
  const [edited, setEdited] = useState<Record<string, Record<string, string>>>({})
  const { mutateAsync: patchTranslations } = usePatchApiCompaniesCurrentTranslationsEntityTypeEntityIdProperty()

  const queries = useQueries({
    queries: properties.map((property) =>
      getGetApiCompaniesCurrentTranslationsEntityTypeEntityIdPropertyQueryOptions(
        entityType,
        entityId ?? '',
        property, {},
        { query: { enabled: !!entityId && enabled } },
      )),
  })

  const loaded = (property: string): Record<string, string> =>
    (queries[properties.indexOf(property)]?.data?.data as Record<string, string> | undefined) ?? {}

  const get = (property: string): Record<string, string> => edited[property] ?? loaded(property)

  const set = (property: string, translations: Record<string, string>) =>
    setEdited((prev) => ({ ...prev, [property]: translations }))

  const reset = () => setEdited({})

  const save = async (targetEntityId: string) => {
    for (const property of properties) {
      const changed = edited[property]
      if (!changed) continue
      if (JSON.stringify(changed) === JSON.stringify(loaded(property))) continue
      await patchTranslations({ entityType, entityId: targetEntityId, property, data: changed })
      await queryClient.invalidateQueries({
        queryKey: getGetApiCompaniesCurrentTranslationsEntityTypeEntityIdPropertyQueryKey(
          entityType, targetEntityId, property,
        ),
      })
    }
  }

  return { get, set, reset, save }
}
